/**
 * useTrainingsFiltering
 *
 * Filter trainings by status, required-only, and search.
 * Assumption: client-side filtering; for large populations use server-side + pagination.
 */

import { useMemo } from 'react';
import type { Training, TrainingsFilters as TrainingsFiltersType } from '../types';
import { defaultTrainingsFilters } from '../types';

function isDefaultStatusSelection(status: TrainingsFiltersType['status']): boolean {
  return (
    status.length === defaultTrainingsFilters.status.length &&
    defaultTrainingsFilters.status.every((s) => status.includes(s))
  );
}

export function useTrainingsFiltering(
  trainings: Training[],
  filters: TrainingsFiltersType
): { filteredTrainings: Training[]; hasActiveFilters: boolean } {
  const filteredTrainings = useMemo(() => {
    return trainings.filter((t) => {
      if (filters.status.length === 0) return false;
      if (!filters.status.includes(t.status)) return false;
      if (filters.requiredOnly && !t.required) return false;
      if (filters.search.trim()) {
        const q = filters.search.toLowerCase();
        if (!t.name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [trainings, filters]);

  const hasActiveFilters =
    !isDefaultStatusSelection(filters.status) ||
    filters.requiredOnly ||
    filters.search.trim().length > 0;

  return { filteredTrainings, hasActiveFilters };
}
